import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import {
  CURRENT_QUESTIONNAIRE_VERSION,
  DEFAULT_USER_PRIORITIES,
  deriveFeatureWeights,
  type FeatureWeights,
  type UserPriorities,
} from "@/lib/userPriorities";
import { useAuth } from "@/components/AuthProvider";
import { useTrainerStepKind } from "@/lib/useTrainerStep";
import { clearTourPending, isTourPending, markTourPending } from "@/lib/pendingTour";
import { claimFirstRunPrompt, noteFirstRunScan } from "@/lib/firstRunPrompts";
import { inProductionMode } from "@/lib/demoMode";
import { shouldRecordOpen, type PaywallSource } from "@/lib/paywallSource";
import { requestDexLiveScan } from "@/lib/hashRouter";
import { apiRequest, apiRequestRaw } from "@/lib/queryClient";
import { fetchLegalCurrent, isConsentCurrent, acknowledgeLegalVersion } from "@/lib/legalConsent";
import { readLocalLegalAck, writeLocalLegalAck } from "@/lib/legalConsentLocal";
import {
  persistThemeMode,
  readPersistedThemeMode,
  readPersistedThemeModeSync,
} from "@/lib/themePersist";
import {
  type AccentTheme,
  persistAccentTheme,
  readPersistedAccentTheme,
  readPersistedAccentThemeSync,
} from "@/lib/accentPersist";
import { syncAppIcon } from "@/lib/appIcon";
import {
  DEFAULT_TRANSFER_SETTINGS,
  type TransferSettings,
} from "@/lib/transferOrganiser";

// Tier values are stable identifiers used throughout the app.
// User-facing labels: "free" → "Basic", "pro" → "Pro", "ultimate" → "Ultimate",
// "trial" → "Trial", "ultimate_trial" → "Ultimate Trial" (1-day, no card).
//
// The "unlocked" tier (a 3-day Pro reward for completing an initial box scan)
// was removed on 2026-07-15. Its only producer, claimBoxScanReward, had no
// callers and no server route, so the reward could never fire; the paywall line
// advertising it was removed at the same time. See docs/pro-gating-audit.md.
//
// Ultimate is the Power Scouter / live battle-overlay tier defined in
// shared/schema.ts. It gates the live battle overlay (J12), unlimited Nex
// chats, and priority Coach inference. Backend slug is "ultimate".
export type Tier =
  | "free"
  | "pro"
  | "ultimate"
  | "trial"
  | "ultimate_trial";
export type ThemeMode = "auto" | "light" | "dark";
// Team accent (Pro faction recolour); canonical definition + persistence in
// lib/accentPersist.ts, re-exported here so UI code has one import site.
export type { AccentTheme } from "@/lib/accentPersist";

// Feature flag — Ultimate is hidden in the shipping product. The code path,
// schema, and helpers are kept intact so the tier can be reactivated without
// re-plumbing. Set to true to surface Ultimate UI again.
// Search the codebase for ULTIMATE_VISIBLE to find every place gated on this.
export const ULTIMATE_VISIBLE = false;

// Pretty label for the current tier, surfaced in UI.
export function tierLabel(t: Tier): string {
  if (t === "pro") return "Pro";
  if (t === "ultimate") return "Ultimate";
  if (t === "ultimate_trial") return "Ultimate Trial";
  if (t === "trial") return "Trial";
  return "Basic";
}

// True if the user currently has access to Pro-tier features (paid Pro or
// trial). Ultimate strictly contains Pro, so it counts too. Use this for any
// gate that's at least Pro — it is the ONLY definition of "has Pro".
export function hasProAccess(t: Tier): boolean {
  return (
    t === "pro" ||
    t === "trial" ||
    t === "ultimate" ||
    t === "ultimate_trial"
  );
}

// True if the user has Ultimate access (paid Ultimate or 24-hour Ultimate
// trial). Use this for the live battle overlay (Power Scouter) and any
// other Ultimate-only entitlements.
export function hasUltimateAccess(t: Tier): boolean {
  return t === "ultimate" || t === "ultimate_trial";
}

// v10: removed the v6 "give a friend a free trial" invite-link system entirely.
// Pro is a self-serve subscription and the only thing we sell.

// ---------------------------------------------------------------------------
// Pokémon Storage Maximisation ("Storage Maximiser")
// ---------------------------------------------------------------------------
// Helps users free up Pokémon GO bag storage by generating in-game search
// strings keyed off their personal protection rules.
//
// 2026-07-15: this is a PRO FEATURE, reached from the Search menu. It was
// previously a Basic-tier add-on with two unlock paths — a $8.99 one-time
// Transfer Organiser IAP, and a "refer 3 friends" funnel. The referral system
// was deleted first (B11); the IAP was withdrawn on 2026-07-15. Neither is
// coming back, so do not reintroduce add-on pricing for this feature.

// User's keep-rule for a category. Most categories follow a small enum.
export type KeepRule =
  | "all"
  | "top1_per_species"
  | "top3_per_species"
  | "top5_per_species"
  | "one_per_form"     // shinies / costumes: keep 1 of each cosmetic variant
  | "only_hundos"
  | "none";

export interface ProtectionProfile {
  // Locked categories (always all). Stored for surface clarity in the wizard.
  hundos: "all";
  bestBuddies: "all";
  tradeReservations: "all";

  // Configurable categories.
  luckies: KeepRule;
  shinies: KeepRule;
  shadows: KeepRule;
  purified: KeepRule;
  fourStars: KeepRule;          // 98%+ IV but not hundo
  costumesAndBackgrounds: KeepRule;
  legacyAndEliteMoves: KeepRule;

  // Numeric thresholds.
  topNPerSpeciesPerLeague: 0 | 1 | 3 | 5;
  rankFloorPercent: number;     // keep anything in top X% rank for any league (0-5)
}

export const DEFAULT_PROTECTION_PROFILE: ProtectionProfile = {
  hundos: "all",
  bestBuddies: "all",
  tradeReservations: "all",
  luckies: "top3_per_species",
  shinies: "one_per_form",
  shadows: "top1_per_species",
  purified: "none",
  fourStars: "top3_per_species",
  costumesAndBackgrounds: "all",
  legacyAndEliteMoves: "all",
  topNPerSpeciesPerLeague: 3,
  rankFloorPercent: 0.5,
};

export interface VerifiedFriend {
  handle: string;
  monogram: string;
  hue: number;
  installedAt: string;          // ISO date the friend installed via the code
  pogoConfirmedAt: string | null; // ISO date both sides confirmed the in-game friendship
}

// B11 (v1.0.10-b2): repriced to the App Store product. Referral path is dead.
/**
 * @deprecated B11 (v1.0.10-b2): the referral path to unlock Storage Maximiser
 * has been removed. The constant is kept exported as 0 so legacy call sites
 * compile while the corresponding UI surfaces are migrated. Storage Maximisation
 * is gated by Pro now (isStorageMaxUnlocked), so this can be deleted once every
 * caller is gone.
 */
export const REFERRALS_REQUIRED_FOR_STORAGE_MAX = 0;

interface SessionState {
  // Legal
  legalAcknowledged: boolean;
  acknowledgeLegal: () => void;
  showLegal: () => void;
  legalOpen: boolean;
  closeLegal: () => void;
  /**
   * The trainer step (name and level, ProfileGate) is up over the app. Timed
   * prompts yield to it like they do to the legal splash.
   */
  trainerStepOpen: boolean;

  // Trade NEW badge
  tradeBadgeSeen: boolean;
  markTradeBadgeSeen: () => void;

  // Paywall / tier
  tier: Tier;
  setTier: (t: Tier) => void;
  /**
   * True once `tier` is real: auth resolved signed-out, or the account's
   * entitlements pull completed. Until then `tier` is the "free" boot
   * placeholder, so anything that treats Basic as a signal (the one-time
   * Why go Pro sheet) must wait for this rather than upsell a Pro trainer.
   */
  tierSettled: boolean;
  trialSearchesLeft: number;
  consumeTrialSearch: () => void;

  // Storage Maximiser. 2026-07-15: it is a PRO FEATURE now, reached from the
  // Search menu. It used to be a standalone one-time purchase (the Transfer
  // Organiser IAP) which has been withdrawn — we no longer sell it separately,
  // so there is no purchase entry point left here.
  //
  // The referral unlock path was removed earlier (B11). The shims below are
  // kept as DEPRECATED no-ops so existing call sites compile.
  referralCode: string;                                  // always "" in B11
  /** @deprecated B11: referral path removed; always empty. */
  verifiedFriends: VerifiedFriend[];
  /** @deprecated B11: referral path removed; always 0. */
  verifiedReferralCount: number;
  /** True iff the trainer can use Storage Maximiser: any Pro-tier trainer, or
   *  anyone grandfathered from the withdrawn add-on. */
  isStorageMaxUnlocked: boolean;
  /** @deprecated B11: referral path removed; no-op. */
  recordReferralInstall: (handle: string, opts?: { monogram?: string; hue?: number }) => void;
  /** @deprecated B11: referral path removed; no-op. */
  confirmReferralInPoGo: (handle: string) => void;

  // B11 (v1.0.10-b2): IAP entitlements (server source of truth, mirrored
  // here from /api/iap/entitlements on app boot and after each purchase).
  entitlements: import("@/lib/iap").IapEntitlements;
  subscription: import("@/lib/iap").IapSubscription;
  /** Drive the native Pro subscription purchase. Pro is the only thing we
   *  sell — the one-time Transfer Organiser IAP was withdrawn on 2026-07-15. */
  purchasePro: (period: "monthly" | "annual") => Promise<{ ok: boolean; message?: string }>;
  /** Refresh the server-side entitlement mirror without driving a purchase. */
  refreshEntitlements: () => Promise<void>;
  /** Drive RevenueCat.restorePurchases for a reinstall. */
  restorePurchases: () => Promise<void>;

  // Per-user protection profile for Storage Maximiser. Wizard writes here.
  protectionProfile: ProtectionProfile;
  setProtectionProfile: (next: ProtectionProfile) => void;
  resetProtectionProfile: () => void;

  // b15a: Transfer Organiser keep/preservation settings. Synced across
  // devices via /api/transfer-settings (user_transfer_settings D1 row).
  transferSettings: TransferSettings;
  setTransferSettings: (next: TransferSettings) => void;
  resetTransferSettings: () => void;

  // Paywall modal
  paywallOpen: boolean;
  /**
   * `source` is a closed union (lib/paywallSource.ts) and is REQUIRED: it is
   * the analytics dimension, and making it optional is how a vocabulary like
   * this rots. `reason` stays free text because it is only ever displayed.
   */
  openPaywall: (source: PaywallSource, reason?: string) => void;
  closePaywall: () => void;
  paywallReason: string | null;
  /** Where the currently-open paywall was opened from. */
  paywallSource: PaywallSource | null;

  // Profile sheet removed in v1.1.3-b4 (Wave 6d / PR #26). The gear icon
  // on the Trainer profile now routes to /settings instead of opening a
  // slide-up drawer. profileOpen/openProfile/closeProfile retired.

  // Theme, Auto follows system; Light/Dark forces.
  // No localStorage (sandbox blocks it). Uses React state + matchMedia.
  themeMode: ThemeMode;
  setThemeMode: (m: ThemeMode) => void;
  isDark: boolean;

  // Team accent (Pro): recolours the brand-green highlight system to the
  // trainer's GO faction — instinct (yellow) / valor (red) / mystic (blue).
  // "nexus" is the default green. The stored choice survives a lapsed
  // subscription; APPLICATION is entitlement-gated, so accentApplied is what
  // the UI actually renders with.
  accentTheme: AccentTheme;
  setAccentTheme: (a: AccentTheme) => void;
  accentApplied: AccentTheme;

  // Dex import state, controls whether the Dex tab shows the big import card
  // or the post-import "keep it current" state. Default = imported (realistic demo).
  hasImported: boolean;
  setHasImported: (b: boolean) => void;

  // Share-to-friend modal state, shared across Trade Tools / Friends
  shareTarget: { numbers: string; label: string } | null;
  openShare: (target: { numbers: string; label: string }) => void;
  closeShare: () => void;

  // Planned events (Calendar tab + Today's Focus)
  plannedEvents: Set<string>;
  togglePlanned: (id: string) => void;

  // Where Nex was opened from (for back button)
  nexFrom: string | null;
  setNexFrom: (path: string | null) => void;

  // Onboarding (v6), runs after legal splash dismissed (first-time only)
  onboardingOpen: boolean;
  showOnboarding: () => void;
  closeOnboarding: () => void;

  // Guided tour (GuidedTour.tsx): the spotlight walkthrough that follows the
  // three onboarding slides on a brand-new account, and is replayable from
  // Settings. The slides sell the app; the tour teaches it.
  tourOpen: boolean;
  /** Which script GuidedTour runs: "intro" (getting started) or a key from
   *  lib/walkthroughs. Read only while tourOpen. */
  tourScript: string;
  showTour: (script?: string) => void;
  closeTour: () => void;

  // Presence, visibility to friends (v6)
  presenceVisible: boolean;
  setPresenceVisible: (b: boolean) => void;

  // Pokémon detail sheet (v6)
  pokemonDetailId: number | null;
  openPokemonDetail: (id: number) => void;
  closePokemonDetail: () => void;

  // ── Chunk B, User Priorities Map ────────────────────────────────────────
  // Single source of truth captured by the Nex companion questionnaire. Powers
  // Custom Dexes, Storage Optimiser defaults, feature elevation, and
  // (Chunk C) friend matching.
  userPriorities: UserPriorities;
  setUserPriorities: (next: UserPriorities) => void;
  resetUserPriorities: () => void;

  // Derived feature elevation weights (recomputed when priorities change).
  // UI surfaces use these to elevate or de-emphasise zone tiles without
  // hiding anything.
  featureWeights: FeatureWeights;

  // Custom dexes, built-ins are seeded from priorities, user-built are
  // appended via the builder. Built-ins can be hidden but not deleted.

  // Nex questionnaire modal state. Triggered after onboarding completes
  // (Get Started tap) and re-openable from Profile to edit priorities.
  nexQuestionnaireOpen: boolean;
  openNexQuestionnaire: () => void;
  closeNexQuestionnaire: () => void;
  // Live Scan entry point. Always funnels into the Pokédex-first scanner
  // (DexLiveScanMenu on /pokedex). The legacy multi-screen LiveScanModal
  // was removed in PR-D3 (2026-06-15) once the Pokédex-first scanner had
  // been the only user-facing path for two release cycles. The `?pivot.
  // legacy=on` rollback flag is gone with it.
  openLiveScan: () => void;
  // Persists the answered priorities, marks completedAt, refreshes built-in
  // custom dexes, and closes the modal.
  completeNexQuestionnaire: (priorities: Omit<UserPriorities, "questionnaireCompletedAt" | "questionnaireVersion">) => void;

  // ── B7 (v1.0.10-b1) -- session continuity ──────────────────────────────
  // One-shot hydrate from /api/user/state on app open. Drives the resume
  // banner, the boot-time Dex headline, and Nex chat history hydrate.
  // Null until the request resolves (or if the user is unauthenticated).
  userState: UserStateBootstrap | null;
  /** Removes the in-flight scan with this id from the local resume queue. */
  dismissResumeBanner: (sessionId: number) => void;
}

// Server response shape from GET /api/user/state. Kept loose so client
// renders gracefully even if the backend grows new fields.
export interface UserStateBootstrap {
  user: {
    id: number;
    username: string | null;
    trainerName: string | null;
    friendCode: string | null;
    pogoAvatarUrl: string | null;
    homeRegion: string | null;
    tier: string;
  } | null;
  recentJournal: Array<{
    id: number;
    occurredAt: number;
    entryType: string;
    speciesId: number | null;
    speciesName: string | null;
    cp: number | null;
    isShiny: boolean | number;
  }>;
  // v1.1.3-b6 (PR #27 resolved): pokedexProgress field retired from the
  // user-state boot payload. Compact-spine Profile reads counts straight
  // from /api/dex/batches.stats now. Two sources of truth removed.
  caughtRecords: number;
  scanQueue: Array<{
    id: number;
    source: string;
    status: string;
    frameIndex: number;
    frameTotal: number | null;
    caught: number;
    seen: number;
    startedAt: number;
    updatedAt: number;
  }>;
  onboardingFlags: {
    hasAvatar: boolean;
    hasFriendCode: boolean;
    hasFirstScan: boolean;
  };
}

// Demo seed: the user has 1 verified friend and 1 install pending PoGo
// confirmation. Helps reviewers see the partial-progress state on the locked
// Storage Maximiser entry point.
//
// In production this is an empty array; the real verifiedFriends list will
// arrive from the friends API once Storage Maximiser is wired to D1.
const INITIAL_VERIFIED_FRIENDS: VerifiedFriend[] = import.meta.env.PROD ? [] : [
  {
    handle: "Volcaron3X",
    monogram: "V",
    hue: 18,
    installedAt: "2026-04-22T18:12:00Z",
    pogoConfirmedAt: "2026-04-23T09:00:00Z",
  },
  {
    handle: "DratiniDad",
    monogram: "D",
    hue: 210,
    installedAt: "2026-04-25T14:40:00Z",
    pogoConfirmedAt: null,
  },
];

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  /*
   * Seeded from the DEVICE, not from false.
   *
   * iOS evicts the app after a spell in the background, so returning to it is a
   * cold start — and this used to begin at `false` and only become true once
   * /api/settings/all answered. That is why the splash reappeared "after a
   * while": not a timeout anywhere, just an app with no memory of having been
   * told, re-asking on every fresh launch, and asking forever when the request
   * failed. The hydrate below still re-checks against the server, so a genuine
   * change to the legal text re-prompts exactly as before.
   */
  const [legalAcknowledged, setLegalAcknowledged] = useState(
    () => readLocalLegalAck(null) != null,
  );
  // The server's active legal version, resolved on hydrate. Null until known
  // (or when the server is unreachable).
  const [legalVersionHash, setLegalVersionHash] = useState<string | null>(null);
  /**
   * Whether the splash is on screen.
   *
   * THIS USED TO BE `useState(true)` — open on every boot, with nothing
   * anywhere consulting whether the trainer had already accepted. Only tapping
   * "I Understand" or the close button ever set it false, and both of those
   * are in-session, so a returning user who had consented months ago met the
   * disclaimer again on every single launch. `legalAcknowledged` was computed
   * correctly the whole time; it was simply never asked.
   *
   * Now it opens only when this device has no record of an acceptance. The
   * hydrate below still re-opens it when the server says the consent on file
   * no longer covers the current text, so a genuine legal change re-prompts
   * exactly as ToS §16 requires — which is the ONLY thing that should bring
   * this back unbidden.
   */
  const [legalOpen, setLegalOpen] = useState(() => readLocalLegalAck(null) == null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // The guided tour. Never auto-fires on its own: Onboarding hands over to it
  // when the trainer taps "Take the tour" on the last slide, and Settings can
  // replay it. Keeping it opt-in means a returning trainer who reinstalls is
  // never trapped in a sixteen step walkthrough they have already seen.
  const [tourOpen, setTourOpen] = useState(false);
  const [tourScript, setTourScript] = useState("intro");
  const [presenceVisible, setPresenceVisible] = useState(true);
  const [pokemonDetailId, setPokemonDetailId] = useState<number | null>(null);
  const [tradeBadgeSeen, setTradeBadgeSeen] = useState(false);
  // v10: invite-link / inviteBonusPacks state removed entirely.

  const [tier, setTier] = useState<Tier>("free");
  const [trialSearchesLeft, setTrialSearchesLeft] = useState(1);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Storage Maximiser add-on state.
  // B11 (v1.0.10-b2): verifiedFriends state is dead but kept as a const
  // empty list so the legacy SessionContext field still resolves. The
  // INITIAL_VERIFIED_FRIENDS demo seed is intentionally unused now.
  const verifiedFriends: VerifiedFriend[] = [];
  const [protectionProfile, setProtectionProfileState] = useState<ProtectionProfile>(DEFAULT_PROTECTION_PROFILE);
  // b15a Transfer Organiser settings. Defaults until hydrated from the server.
  const [transferSettings, setTransferSettingsState] = useState<TransferSettings>(DEFAULT_TRANSFER_SETTINGS);
  // Chunk B: priorities map + dependent state.
  const [userPriorities, setUserPrioritiesState] = useState<UserPriorities>(DEFAULT_USER_PRIORITIES);
  // Seed built-in custom dexes from defaults so the Custom Dexes page works
  // even before the user completes the questionnaire.
  const [nexQuestionnaireOpen, setNexQuestionnaireOpen] = useState(false);
  // Referral code: in production, derive from the signed-in user's username
  // so each account gets their own. The demo prototype keeps a fixed vanity
  // string so screenshots stay deterministic.
  const auth = useAuth();
  const referralCode = useMemo(() => {
    // Demo/screenshot deterministic referral. Wrapped in
    // import.meta.env.PROD so the literal is tree-shaken out of the
    // production bundle (no name leaks in dist).
    if (!import.meta.env.PROD && !inProductionMode()) return "INFERNALVAMP-7K2X";
    // Referral code is built from the PoGo identity ONLY (trainer name or
    // its slug). Never use the IdP displayName or email local-part: those
    // can contain the user's real name and must not be exposed publicly.
    const handle =
      auth.account?.trainerName ||
      auth.account?.username ||
      "";
    if (!handle) return "";
    // Stable suffix derived from the account id keeps the code unique even
    // for users who pick the same handle.
    const suffix = (auth.account?.id ?? 0).toString(36).toUpperCase().padStart(4, "0");
    return `${handle.toUpperCase().replace(/[^A-Z0-9]/g, "")}-${suffix}`;
  }, [auth.account?.id, auth.account?.username, auth.account?.trainerName]);

  // Tick once a minute so the unlock countdown updates and the tier flips
  // back to "free" when the window expires.
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Fire the onboarding walkthrough on brand-new sign-ups only. We treat an
  // account as "new" if its createdAt timestamp is within the last 60s of the
  // moment we FIRST see it on this device. Returning users (whose account is
  // older than 60s when restored from a stored token, or who simply sign back
  // in) skip onboarding entirely.
  //
  // Ordering: two things sit in front of the app at sign-up, and the tour
  // waits for both. The legal splash sits above everything. Under it is the
  // trainer step (two typed fields, name and level, since 2026-10-02): it is
  // open while the account has no trainer name and the step is still asked for
  // (lib/profileSetupRequest), and it ends by being filled in OR by "Not now",
  // so nobody who puts it off loses the tour (the old screenshot wall's "Look
  // around first" did exactly that). Either can take longer than the 60s "new
  // account" window, so a new account seen while one is up is remembered in
  // `pendingOnboarding` and greeted once both are gone, rather than re-checking
  // the age then. The memory is mirrored to the device (lib/pendingTour): the
  // trainer step sends people to Pokémon GO to check their name, and iOS can
  // evict the app behind it, so a ref alone lost the tour on the way back.
  //
  // We track which account ids we've already greeted in a ref so React
  // StrictMode double-invocations and re-renders don't re-open the tour.
  const greetedAccountsRef = useRef<Set<number>>(new Set());
  const pendingOnboardingRef = useRef<Set<number>>(new Set());
  // The same rule the ProfileGate renders by (lib/useTrainerStep), so the tour
  // and the timed prompts yield to the step whenever it is actually up: at
  // sign-up, from Live Scan, and for the once-per-account confirm.
  const trainerStepOpen = useTrainerStepKind(auth.account) != null;
  useEffect(() => {
    const acct = auth.account;
    if (!acct) return;
    if (greetedAccountsRef.current.has(acct.id)) return;

    const createdMs = typeof acct.createdAt === "string"
      ? Date.parse(acct.createdAt)
      : Number(acct.createdAt);
    if (!Number.isFinite(createdMs)) return;
    const ageMs = Date.now() - createdMs;
    // 60s window catches signup, signInWithGoogle, signInWithApple paths
    // where findOrCreateOAuthAccount minted a fresh account record.
    const isNewAccount = ageMs >= 0 && ageMs < 60_000;
    const isPending = pendingOnboardingRef.current.has(acct.id) || isTourPending(acct.id);
    if (!isNewAccount && !isPending) return;

    if (legalOpen || trainerStepOpen) {
      // The legal splash or the trainer step is up (both cover the tour).
      // Greet when they are gone.
      pendingOnboardingRef.current.add(acct.id);
      markTourPending(acct.id);
      return;
    }

    // Signed up, past the legal splash and the trainer step. Greet exactly once.
    //
    // STRAIGHT TO THE TOUR (2026-08-17). This used to open the three intro
    // slides, whose last button started the tour, which then fired the seven-
    // question Nex questionnaire. That is three explanatory steps stacked in
    // front of a trainer who has not yet scanned anything.
    //
    // The slides tell you what the app is for; the tour SHOWS you, on the real
    // screens, and it is the better of the two. So the slides go and the tour
    // stays. Both remain available on demand (showOnboarding / showTour) for
    // Settings and for anyone who wants the recap.
    pendingOnboardingRef.current.delete(acct.id);
    clearTourPending(acct.id);
    greetedAccountsRef.current.add(acct.id);
    // Day one is the tour's: the optional asks (lib/firstRunPrompts) see this
    // session as spent and wait for a later one.
    claimFirstRunPrompt("tour");
    setTourOpen(true);
  }, [auth.account, legalOpen, trainerStepOpen]);

  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallReason, setPaywallReason] = useState<string | null>(null);
  const [paywallSource, setPaywallSource] = useState<PaywallSource | null>(null);

  // Theme state.
  //
  // We seed from the synchronous localStorage mirror so the very first paint
  // already matches the user's choice (no light → dark flash). The Capacitor
  // Preferences store is read async right after mount as a second-chance
  // fallback for native runtimes where localStorage was cleared but the
  // native key-value store still has the preference.
  // Default is "auto" (system-driven) until the trainer picks a mode; from
  // then on their pick is sticky and survives relaunch via the two local
  // mirrors in themePersist.ts.
  const [themeMode, setThemeModeState] = useState<ThemeMode>(
    () => readPersistedThemeModeSync() ?? "auto",
  );
  // Set once the trainer picks a mode themselves. The async hydration and the
  // server reconcile below both defer to it, so an explicit choice is never
  // overwritten by a slower store that still holds the previous value.
  const themeChosenByUserRef = useRef(false);
  const setThemeMode = (next: ThemeMode) => {
    themeChosenByUserRef.current = true;
    setThemeModeState(next);
    persistThemeMode(next);
  };
  // Async hydration from Capacitor Preferences: a second-chance read for
  // native runtimes where localStorage was cleared but the native store still
  // holds the preference. Skipped once the trainer has chosen in-session.
  // (The guard was described here before but never actually implemented, so a
  // choice made while this read was in flight could be clobbered by the stale
  // value it returned.)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await readPersistedThemeMode();
      if (cancelled || themeChosenByUserRef.current) return;
      if (stored && stored !== themeMode) {
        setThemeModeState(stored);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ── Team accent (Pro faction recolour) ──────────────────────────────────
  // Same three-store strategy as the theme above: synchronous localStorage
  // seed (no green→faction flash), async Capacitor Preferences second chance,
  // server reconcile in the PR-A hydration below. The choice is stored even
  // for free trainers (picked during a trial, kept after lapse); whether it
  // is APPLIED is entitlement-gated where accentApplied is derived.
  const [accentTheme, setAccentThemeState] = useState<AccentTheme>(
    () => readPersistedAccentThemeSync() ?? "nexus",
  );
  const accentChosenByUserRef = useRef(false);
  const setAccentTheme = (next: AccentTheme) => {
    accentChosenByUserRef.current = true;
    setAccentThemeState(next);
    persistAccentTheme(next);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await readPersistedAccentTheme();
      if (cancelled || accentChosenByUserRef.current) return;
      if (stored && stored !== accentTheme) {
        setAccentThemeState(stored);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [systemDark, setSystemDark] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;
  });

  // Listen to system preference changes, only effective when mode == auto
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, []);

  // v1.2.1(13): The unauthenticated landing surfaces (Welcome / Login /
  // Signup) are dark-only by brand. Force dark whenever the user is not
  // signed in, regardless of their saved theme. Once they sign in the
  // saved preference (auto / light / dark) takes over.
  const userPreferredDark =
    themeMode === "auto" ? systemDark : themeMode === "dark";
  const isDark = !auth.isSignedIn ? true : userPreferredDark;

  // Toggle the `dark` class on <html> and keep the native iOS/Android status
  // bar glyphs matched to the theme. Without the native sync the system clock
  // / battery / wifi stay light-on-light in light mode (b4 evidence shots).
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) root.classList.add("dark");
    else root.classList.remove("dark");
    root.style.colorScheme = isDark ? "dark" : "light";
    void (async () => {
      const { syncStatusBarTheme } = await import("@/lib/nativeStatusBar");
      await syncStatusBarTheme(isDark);
    })();
    // iOS can reset the status-bar style when the app returns from
    // background; re-apply on resume so the glyphs don't revert to the
    // static config's dark style while the user is in light mode.
    let remove: (() => void) | undefined;
    void (async () => {
      try {
        const { App } = await import("@capacitor/app" as any);
        const handle = await App.addListener("appStateChange", (s: { isActive: boolean }) => {
          if (!s?.isActive) return;
          void import("@/lib/nativeStatusBar").then((m) => m.syncStatusBarTheme(isDark));
        });
        remove = () => { void handle.remove(); };
      } catch {
        /* not a Capacitor runtime; nothing to listen to. */
      }
    })();
    return () => { remove?.(); };
  }, [isDark]);

  // Apply the team accent as a data attribute on <html>. accents.css keys its
  // token overrides off html[data-accent="…"] (composing with the .dark class
  // the theme effect above manages). Entitlement-gated here: a lapsed Pro
  // falls back to brand green automatically while the stored pick survives.
  const accentApplied: AccentTheme = hasProAccess(tier) ? accentTheme : "nexus";
  useEffect(() => {
    const root = document.documentElement;
    if (accentApplied === "nexus") root.removeAttribute("data-accent");
    else root.setAttribute("data-accent", accentApplied);
  }, [accentApplied]);

  // True once the boot placeholder tier ("free") has been confirmed or
  // replaced: auth resolved signed-out, or the account's entitlements pull
  // completed (setTierSettled in the entitlements effect below). Declared
  // here, ABOVE the icon-sync effect that lists it as a dependency.
  const [tierSettled, setTierSettled] = useState(false);
  useEffect(() => {
    if (!auth.isRestoring && !auth.account) setTierSettled(true);
  }, [auth.isRestoring, auth.account]);

  // Keep the launcher icon matched to the Pro faction accent (the only thing
  // that moves it since the light variant retired, 2026-08-27). syncAppIcon
  // no-ops off-iOS and when the icon already matches, so boot never alerts.
  //
  // Gated on tierSettled: `tier` boots as "free" until entitlements hydrate,
  // so an ungated sync on a Light + faction-accent device resolved the LIGHT
  // icon first (faction branch needs Pro), swapped the launcher icon with an
  // iOS alert, then swapped BACK once Pro arrived — a blue→light→blue flap on
  // every app open (device report 2026-08-27). The icon now waits until the
  // tier is actually known: signed-out resolution, or entitlements pulled.
  useEffect(() => {
    if (!tierSettled) return;
    void syncAppIcon(themeMode, accentTheme, hasProAccess(tier));
  }, [tierSettled, themeMode, accentTheme, tier]);

  // The per-account hydrates below are keyed on the account ID, never on the
  // account object. A cold start publishes the cached account and then swaps
  // in the /api/auth/me payload, and every profile edit swaps it again: a new
  // object for the same trainer. Keyed on the object, each swap ran the
  // effect's cleanup, which cancelled the hydrate in flight, and the re-run
  // declined to start another (client/src/test/sessionHydrateAuth.test.tsx).
  const accountId = auth.account?.id ?? null;

  // A hydrate that FAILED (offline launch, a 5xx) is owed another try, because
  // the persist effects below wait for one to succeed. Coming back online or
  // back to the foreground asks again; nothing polls.
  const hydrateOwedRef = useRef(false);
  const [hydrateRetry, setHydrateRetry] = useState(0);
  useEffect(() => {
    const retry = () => {
      if (!hydrateOwedRef.current || document.visibilityState === "hidden") return;
      hydrateOwedRef.current = false;
      setHydrateRetry((n) => n + 1);
    };
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", retry);
    return () => {
      window.removeEventListener("online", retry);
      document.removeEventListener("visibilitychange", retry);
    };
  }, []);

  // ── PR-A: hydrate persistent session state from /api/settings/all ───────
  //
  // On every sign-in (account id flip), pull the user's saved theme,
  // legal-acknowledged stamp, trade-badge-seen stamp, and box-scan-reward
  // claim so the next session lands the user exactly where they left off.
  //
  // `settingsHydratedFor` is the account whose saved settings have actually
  // been READ into state. It is set when the hydrate succeeds, not when it
  // starts: it is what the persist effect below waits on, and "started" is
  // not a reason to believe the state is anything but defaults. Once it names
  // this account the hydrate does not run again, so a later response can never
  // clobber an edit made in the meantime.
  const [settingsHydratedFor, setSettingsHydratedFor] = useState<number | null>(null);
  useEffect(() => {
    if (accountId == null) return;
    if (settingsHydratedFor === accountId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest("GET", "/api/settings/all");
        const body = await res.json();
        if (cancelled) return;
        const s = body?.settings ?? {};
        if (s.themeMode === "auto" || s.themeMode === "light" || s.themeMode === "dark") {
          // Only adopt the server-side theme if the trainer hasn't already
          // chosen one locally; the local preference is the source of truth
          // for the user's most recent action. This avoids a network-bound
          // race where the server's stale value overwrites a fresh toggle.
          // The ref covers the case the localStorage mirror cannot: a native
          // runtime with storage blocked, where a fresh in-session pick leaves
          // readPersistedThemeModeSync() still null.
          if (!themeChosenByUserRef.current && !readPersistedThemeModeSync()) {
            setThemeModeState(s.themeMode);
            persistThemeMode(s.themeMode);
          }
        }
        if (
          s.accentTheme === "nexus" || s.accentTheme === "instinct" ||
          s.accentTheme === "valor" || s.accentTheme === "mystic"
        ) {
          // Same local-first rule as the theme: the server value only fills
          // a device that has no local choice yet.
          if (!accentChosenByUserRef.current && !readPersistedAccentThemeSync()) {
            setAccentThemeState(s.accentTheme);
            persistAccentTheme(s.accentTheme);
          }
        }
        // Re-consent check: an acknowledgement only counts if it was made
        // against the CURRENT legal text. `legal_version_hash` is what the
        // account last accepted; /api/legal/current is what the server expects
        // now. When a legal document changes, the hash moves and the splash
        // re-shows (ToS §16's notice promise). Fetched here rather than at
        // module scope so an offline boot degrades to "trust the existing ack".
        const current = await fetchLegalCurrent();
        if (cancelled) return;
        setLegalVersionHash(current?.hash ?? null);
        const stillCurrent = isConsentCurrent({
          acknowledgedAt: s.legalAcknowledgedAt,
          acknowledgedHash: s.legalVersionHash,
          currentHash: current?.hash,
        });
        setLegalAcknowledged(stillCurrent);
        // A genuine change to the legal text is the one thing that may put the
        // splash back in front of someone who has already accepted.
        if (!stillCurrent) setLegalOpen(true);
        // MIGRATION, and the reason this is not just an open: everyone who
        // accepted before the device-local record existed has a perfectly good
        // server-side consent and no local one, so the boot default would ask
        // them once more for nothing. Server says we are covered and this
        // device has no note of it — write the note and take the splash down.
        //
        // Guarded on the record being ABSENT, which is also what keeps it from
        // yanking away a splash the trainer deliberately opened from Settings:
        // anyone who can do that has accepted already, so they have a record.
        else if (readLocalLegalAck(accountId) == null) {
          writeLocalLegalAck(accountId, current?.hash ?? null);
          setLegalOpen(false);
        }
        if (s.tradeBadgeSeenAt) setTradeBadgeSeen(true);
        setSettingsHydratedFor(accountId);
      } catch (err) {
        if (cancelled) return;
        console.warn("[SessionProvider] settings hydrate failed", err);
        hydrateOwedRef.current = true;
        // Offline, or the server is down. The device's own record is the honest
        // answer here — re-showing the splash to someone who has already
        // accepted, because we could not ask, is the worst of both.
        const local = readLocalLegalAck(accountId);
        if (local) setLegalAcknowledged(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, settingsHydratedFor, hydrateRetry]);

  // Persist user-driven changes back to /api/settings/all with an 800ms
  // debounce so we don't fire a request on every keystroke / theme toggle.
  // We skip persistence until the hydrate has SUCCEEDED for this account to
  // avoid overwriting the server's authoritative state with our defaults.
  // (It used to wait only for the hydrate to have started, so one that failed
  // was followed 800ms later by `tradeBadgeSeenAt: null` and the default theme.)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (accountId == null) return;
    if (settingsHydratedFor !== accountId) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(async () => {
      try {
        // The settings endpoint stores these as SQLite INTEGER unix-ms
        // timestamps (cloudflare/src/db/schema.ts) and validates via
        // z.number().int() (cloudflare/src/routes/settings.ts). Sending
        // ISO strings produced the 400
        //   "legalAcknowledgedAt expected number received string"
        // on iOS 1.2.1(5). Use Date.now() (epoch ms) for all three.
        const nowMs = Date.now();
        // boxScanRewardClaimedAt is no longer sent: the 3-day box-scan reward
        // was removed on 2026-07-15. The column stays in the schema (dropping
        // it is a migration for no gain) and the field stays optional on the
        // settings route, so omitting it here is safe and simply leaves any
        // historical value untouched.
        //
        // legalAcknowledgedAt is no longer sent either: POST /api/legal/acknowledge
        // owns it now, because it must be written together with the version hash
        // that was accepted. Sending it from here would also clear the timestamp
        // whenever a pending re-consent flipped legalAcknowledged to false —
        // destroying the record that the user consented to the PREVIOUS version.
        await apiRequest("PUT", "/api/settings/all", {
          themeMode,
          accentTheme,
          tradeBadgeSeenAt: tradeBadgeSeen ? nowMs : null,
        });
      } catch (err) {
        // Local theme is already mirrored to localStorage + Capacitor
        // Preferences via persistThemeMode(), so the user's choice survives
        // a relaunch even when this server PUT fails (offline boot, 5xx,
        // expired session). Log at info level so the iOS console isn't
        // peppered with red on a routine offline launch.
        const msg = err instanceof Error ? err.message : String(err ?? "");
        console.info("[SessionProvider] settings persist failed (local mirror retained)", msg);
      }
    }, 800);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [accountId, settingsHydratedFor, themeMode, accentTheme, tradeBadgeSeen]);

  // ── b15a: Transfer Organiser settings sync ──────────────────────────────
  // Hydrate once per account from /api/transfer-settings, then persist edits
  // back with an 800ms debounce. Mirrors the /api/settings/all pattern above
  // (including persisting only once the hydrate has succeeded: this PUT is the
  // whole settings object, so before that it is DEFAULT_TRANSFER_SETTINGS over
  // what the trainer saved) but lives on its own route + D1 table
  // (user_transfer_settings) so the keep-top-N + preservation toggles sync
  // across the trainer's devices.
  const [transferHydratedFor, setTransferHydratedFor] = useState<number | null>(null);
  useEffect(() => {
    if (accountId == null) return;
    if (transferHydratedFor === accountId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest("GET", "/api/transfer-settings");
        const body = await res.json();
        if (cancelled) return;
        const s = body?.settings;
        if (s) {
          setTransferSettingsState({
            keepTopN: Math.max(1, Math.min(10, Number(s.keepTopN ?? DEFAULT_TRANSFER_SETTINGS.keepTopN))),
            preserveShiny: Boolean(s.preserveShiny),
            preserveLucky: Boolean(s.preserveLucky),
            preserveXxs: Boolean(s.preserveXxs),
            preservePerfect: Boolean(s.preservePerfect),
            preserveNundo: Boolean(s.preserveNundo),
            evolutionProtect: Boolean(s.evolutionProtect),
          });
        }
        setTransferHydratedFor(accountId);
      } catch (err) {
        if (cancelled) return;
        console.warn("[SessionProvider] transfer-settings hydrate failed", err);
        hydrateOwedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, transferHydratedFor, hydrateRetry]);

  const transferPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (accountId == null) return;
    if (transferHydratedFor !== accountId) return;
    if (transferPersistTimerRef.current) clearTimeout(transferPersistTimerRef.current);
    transferPersistTimerRef.current = setTimeout(async () => {
      try {
        await apiRequest("PUT", "/api/transfer-settings", transferSettings);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err ?? "");
        console.info("[SessionProvider] transfer-settings persist failed", msg);
      }
    }, 800);
    return () => {
      if (transferPersistTimerRef.current) clearTimeout(transferPersistTimerRef.current);
    };
  }, [accountId, transferHydratedFor, transferSettings]);

  // B7 (v1.0.10-b1): one-shot bootstrap from /api/user/state.
  // Null until the first request resolves. Resume-banner UI dismisses by
  // id so a user can dismiss a stale row without re-fetching the world.
  const [userState, setUserState] = useState<UserStateBootstrap | null>(null);
  const userStateHydratedRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (accountId == null) return;
    if (userStateHydratedRef.current.has(accountId)) return;
    userStateHydratedRef.current.add(accountId);
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest("GET", "/api/user/state");
        const body = (await res.json()) as UserStateBootstrap;
        if (!cancelled) setUserState(body);
        // A trainer with dex rows on the server has used the app, whichever
        // device they used it on: the feedback and review asks may count it.
        if (body?.onboardingFlags?.hasFirstScan) noteFirstRunScan();
      } catch (err) {
        // Hydration failures are non-fatal: the rest of the app falls back
        // to per-page React Query fetches. Log so we can spot a 5xx in
        // prod without bricking the boot.
        console.warn("[SessionProvider] user state hydrate failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId]);
  const dismissResumeBanner = (sessionId: number) => {
    setUserState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        scanQueue: prev.scanQueue.filter((s) => s.id !== sessionId),
      };
    });
  };

  // Demo default: assume the user has already done their initial import
  // (this is the realistic ongoing-use state per feedback).
  const [hasImported, setHasImported] = useState(true);

  const [shareTarget, setShareTarget] = useState<{ numbers: string; label: string } | null>(null);
  const [plannedEvents, setPlannedEvents] = useState<Set<string>>(new Set());
  const [nexFrom, setNexFrom] = useState<string | null>(null);

  const togglePlanned = (id: string) => {
    setPlannedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const acknowledgeLegal = () => {
    setLegalAcknowledged(true);
    setLegalOpen(false);
    // Persist consent against the version actually being accepted, so a later
    // legal change moves the hash and re-prompts. Fire-and-forget: the local
    // state is optimistic and the next hydrate re-checks against the server,
    // so a failed write costs at most one extra prompt.
    void acknowledgeLegalVersion(legalVersionHash);
    // ...and remember it on this device, so the next cold start does not ask
    // again before the server has had a chance to answer.
    writeLocalLegalAck(auth.account?.id ?? null, legalVersionHash);
    // Onboarding is no longer fired here. It is tied to brand-new sign-ups
    // only (see the auth.account effect below) so returning users who simply
    // re-accept legal updates do not get walked through the slides again.
  };
  const showLegal = () => setLegalOpen(true);
  const closeLegal = () => setLegalOpen(false);
  const markTradeBadgeSeen = () => setTradeBadgeSeen(true);

  const openPaywall = (source: PaywallSource, reason?: string) => {
    setPaywallSource(source);
    setPaywallReason(reason ?? null);
    setPaywallOpen(true);

    // Record that a Pro wall was SHOWN here (migration 0071). This is the "what
    // makes people stop and consider Pro" signal, and it is far larger than the
    // buy-tap one — most people who hit a wall never tap.
    //
    // Deliberately not awaited: opening the paywall must be instant, and a
    // failed analytics write must never delay or block the modal.
    if (shouldRecordOpen(source)) {
      void apiRequestRaw("POST", "/api/iap/paywall-intent", {
        source,
        stage: "open",
      }).catch(() => {
        /* interest data is nice to have; never surface this */
      });
    }
  };
  const closePaywall = () => setPaywallOpen(false);

  const consumeTrialSearch = () => setTrialSearchesLeft((n) => Math.max(0, n - 1));

  const handleSetTier = (t: Tier) => {
    setTier(t);
    if (t === "trial") setTrialSearchesLeft(1);
  };

  // ── B11 (v1.0.10-b2) IAP entitlements (server source of truth) ────────
  // Hydrated once per account on auth, refreshed after every purchase.
  const [entitlements, setEntitlements] = useState<import("@/lib/iap").IapEntitlements>({
    pro: false,
    passport: false,
  });
  const [subscription, setSubscription] = useState<import("@/lib/iap").IapSubscription>({
    tier: "basic",
    expiresAt: null,
  });
  const entitlementsHydratedAccountRef = useRef<Set<number>>(new Set());

  async function pullEntitlements(): Promise<void> {
    const { fetchEntitlements, readNativeEntitlements } = await import("@/lib/iap");
    const ent = await fetchEntitlements();

    // The server mirror is the source of truth, but it has a known failure
    // mode: a promotional/comp grant whose webhook was dropped leaves the
    // server saying pro:false for someone the store considers Pro. Ask the RC
    // SDK too and take the union — the SDK can only ever ADD an entitlement
    // here, never remove one the server granted, so a stale/offline SDK read
    // cannot lock anyone out. Also covers the seconds between a purchase and
    // its webhook landing.
    const native = await readNativeEntitlements().catch(() => null);

    // Server unreachable AND the SDK knows nothing: leave state untouched.
    if (!ent && !native) return;

    const merged = {
      pro: Boolean(ent?.entitlements.pro || native?.pro),
      passport: Boolean(ent?.entitlements.passport || native?.passport),
    };
    setEntitlements(merged);
    if (ent) setSubscription(ent.subscription);
    // Mirror to the legacy tier so isPro / hasProAccess gates downstream
    // resolve correctly without changing every consumer in one PR.
    if (merged.pro && tier === "free") {
      setTier("pro");
    }
  }

  useEffect(() => {
    if (accountId == null) return;
    if (entitlementsHydratedAccountRef.current.has(accountId)) return;
    entitlementsHydratedAccountRef.current.add(accountId);
    let cancelled = false;
    (async () => {
      try {
        const { initIAP } = await import("@/lib/iap");
        await initIAP({ accountId });
      } catch (err) {
        console.warn("[SessionProvider] initIAP failed", err);
      }
      // 2026-10-13: tie the install to the account for attribution and log the
      // registration once for a brand-new account (email, Google and Apple all
      // arrive here). No-op on web / when attribution is off.
      try {
        const acct = auth.account;
        if (acct && acct.id === accountId) {
          const { identifyAttribution } = await import("@/lib/attribution");
          await identifyAttribution({ id: acct.id, createdAt: acct.createdAt });
        }
      } catch (err) {
        console.warn("[SessionProvider] identifyAttribution failed", err);
      }
      if (cancelled) return;
      await pullEntitlements();
      if (!cancelled) setTierSettled(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const refreshEntitlements = async (): Promise<void> => {
    await pullEntitlements();
  };

  const purchasePro = async (period: "monthly" | "annual"): Promise<{ ok: boolean; message?: string }> => {
    const { purchase, IAP_PRODUCT_IDS } = await import("@/lib/iap");
    const productId = period === "annual" ? IAP_PRODUCT_IDS.proAnnual : IAP_PRODUCT_IDS.proMonthly;

    // Record where this purchase is being made from BEFORE handing off to the
    // store. After `purchase()` the app may be backgrounded behind the store
    // sheet or killed outright, and the RevenueCat webhook that follows knows
    // nothing about our UI — so writing it afterwards would lose exactly the
    // attributions that matter. Awaited but never fatal: a trainer who paid
    // gets their Pro whether or not we worked out where they tapped.
    try {
      await apiRequestRaw("POST", "/api/iap/paywall-intent", {
        source: paywallSource ?? "unknown",
        stage: "tap",
        plan: period,
      });
    } catch {
      /* attribution is reporting; never block a purchase on it */
    }

    const result = await purchase(productId);
    if (result.ok) {
      // Optimistic local update; the webhook-driven server state lands
      // within a few seconds and pullEntitlements() syncs.
      setEntitlements((e) => ({ ...e, pro: true }));
      setSubscription((s) => ({ ...s, tier: "pro" }));
      await pullEntitlements();
      return { ok: true };
    }
    return { ok: false, message: result.reason === "userCancelled" ? undefined : result.message };
  };

  const restorePurchases = async (): Promise<void> => {
    const { restorePurchases: restore, readNativeEntitlements } = await import("@/lib/iap");
    const restored = await restore();
    // Restore is exactly when trusting the SDK matters most: the trainer is
    // explicitly telling us they own something. `restore()` returns null when
    // the SERVER leg fails even though the native restore succeeded, so fall
    // back to asking the SDK directly rather than silently doing nothing.
    const native = await readNativeEntitlements().catch(() => null);
    const merged = restored || native
      ? {
          pro: Boolean(restored?.pro || native?.pro),
          passport: Boolean(restored?.passport || native?.passport),
        }
      : null;
    if (merged) {
      setEntitlements(merged);
      if (merged.pro && tier === "free") setTier("pro");
    }
    // Then the grant-aware read (2026-09-22): the restore route only ever
    // vouched for store purchases, so an admin-granted Pro that tapped
    // Restore from the paywall saw Pro vanish until the next app open.
    await pullEntitlements();
  };

  // ── Storage Maximiser gating ──────────────────────────────────────────
  // 2026-07-15: Storage Maximisation is part of Pro. The Transfer Organiser
  // one-time IAP that used to gate it was withdrawn and confirmed unsold, so
  // Pro is the only way in — no add-on entitlement, no grandfather clause.
  const verifiedReferralCount = 0; // referral path removed
  const isStorageMaxUnlocked = hasProAccess(tier);

  // Referral helpers retained as no-op shims for legacy call sites.
  // Once every consumer has been migrated to the entitlements pattern
  // these can be deleted.
  const recordReferralInstall = (
    _handle: string,
    _opts?: { monogram?: string; hue?: number },
  ) => {
    /* B11: referral path removed. */
  };
  const confirmReferralInPoGo = (_handle: string) => {
    /* B11: referral path removed. */
  };

  const setProtectionProfile = (next: ProtectionProfile) => setProtectionProfileState(next);
  const resetProtectionProfile = () => setProtectionProfileState(DEFAULT_PROTECTION_PROFILE);

  const setTransferSettings = (next: TransferSettings) => setTransferSettingsState(next);
  const resetTransferSettings = () => setTransferSettingsState(DEFAULT_TRANSFER_SETTINGS);

  // ── Chunk B, User Priorities actions ──────────────────────────────────
  // The seeded "built-in custom dexes" that used to be refreshed here went with
  // the custom-dex builder (2026-08-14). Priorities still drive the radar, the
  // event cross-references and the trophy slot; they no longer manufacture
  // dexes.
  const setUserPriorities = (next: UserPriorities) => {
    setUserPrioritiesState(next);
  };

  const resetUserPriorities = () => {
    setUserPrioritiesState(DEFAULT_USER_PRIORITIES);
  };

  const completeNexQuestionnaire = (
    priorities: Omit<UserPriorities, "questionnaireCompletedAt" | "questionnaireVersion">,
  ) => {
    const next: UserPriorities = {
      ...priorities,
      // Pinned friends are a Settings choice, not a questionnaire answer, so
      // they must survive a re-run of the questionnaire (whose draft object
      // never carries them). Carry the current pins forward unless the caller
      // explicitly supplied their own.
      priorityFriendIds:
        priorities.priorityFriendIds ?? userPriorities.priorityFriendIds ?? [],
      questionnaireCompletedAt: new Date().toISOString(),
      questionnaireVersion: CURRENT_QUESTIONNAIRE_VERSION,
    };
    setUserPriorities(next);
    setNexQuestionnaireOpen(false);
  };

  const featureWeights = useMemo(() => deriveFeatureWeights(userPriorities), [userPriorities]);

  // Custom dex CRUD.
  const openNexQuestionnaire = () => setNexQuestionnaireOpen(true);
  const closeNexQuestionnaire = () => setNexQuestionnaireOpen(false);

  /**
   * FIRST RUN ENDS AT THE TOUR. The Nex questionnaire used to fire itself here
   * as a fourth automatic step, and it no longer does (2026-08-17).
   *
   * WHY
   *   Signing up already costs a trainer four gates before they can do the
   *   thing they installed the app for: the mandatory Pokémon GO profile
   *   screenshot (which means LEAVING the app, opening GO, screenshotting and
   *   coming back), three onboarding slides, the guided tour, and then seven
   *   questions. Of the 32 real accounts that never wrote a single dex row, 16
   *   never got past the screenshot gate and 12 more cleared it and still never
   *   scanned — and activation, when it happens, happens within ~7 minutes of
   *   signup. Every step before the first scan is spending a budget we can
   *   measure running out.
   *
   *   The questionnaire is the cheapest of those to drop because nothing waits
   *   on it: userPriorities falls back to DEFAULT_USER_PRIORITIES, so Custom
   *   Dexes, the Storage Optimiser and feature elevation all work on the
   *   balanced defaults until the trainer chooses to tune them.
   *
   * IT IS NOT REMOVED, ONLY UN-AUTOMATED. Every deliberate entry point still
   * opens it — Settings, the Profile priority radar (which shows a "set your
   * priorities" CTA precisely for this), the Storage Optimiser and the Hundo
   * dex section — and those reach a trainer who has seen what the answers
   * change, which is when the questions are worth asking.
   */
  // Every "Live Scan" entry point in the product funnels into the
  // Pokédex-only DexLiveScanMenu (mounted on /pokedex). The legacy multi-
  // screen LiveScanModal was removed in PR-D3 (2026-06-15). SessionProvider
  // lives outside wouter's <Router>, so we navigate by setting the hash
  // location the app's useHashLocation router reads, rather than calling
  // useLocation() here.
  const openLiveScan = () => {
    requestDexLiveScan();
  };

  return (
    <SessionContext.Provider
      value={{
        legalAcknowledged, acknowledgeLegal, showLegal, legalOpen, closeLegal,
        trainerStepOpen,
        tradeBadgeSeen, markTradeBadgeSeen,
        tier, setTier: handleSetTier,
        tierSettled,
        trialSearchesLeft, consumeTrialSearch,
        referralCode,
        verifiedFriends,
        verifiedReferralCount,
        isStorageMaxUnlocked,
        recordReferralInstall,
        confirmReferralInPoGo,
        entitlements,
        subscription,
        purchasePro,
        refreshEntitlements,
        restorePurchases,
        protectionProfile,
        setProtectionProfile,
        resetProtectionProfile,
        transferSettings,
        setTransferSettings,
        resetTransferSettings,
        paywallOpen, openPaywall, closePaywall, paywallReason, paywallSource,
        themeMode, setThemeMode, isDark,
        accentTheme, setAccentTheme, accentApplied,
        hasImported, setHasImported,
        shareTarget,
        openShare: (t) => setShareTarget(t),
        closeShare: () => setShareTarget(null),
        plannedEvents,
        togglePlanned,
        nexFrom,
        setNexFrom,
        onboardingOpen,
        showOnboarding: () => setOnboardingOpen(true),
        closeOnboarding: () => {
          setOnboardingOpen(false);
          // Sync "saw the intro slides" to the server (migration 0052) so the
          // admin Deletions/Accounts views can answer onboarding questions.
          // Fire-and-forget: a signed-out or offline close just skips the stamp.
          apiRequest("PATCH", "/api/account/settings", { onboardingSeenAt: Date.now() }).catch(() => {});
        },
        tourOpen,
        tourScript,
        showTour: (script = "intro") => {
          // The tour drives navigation itself, so make sure nothing else is
          // holding the screen when it starts.
          setOnboardingOpen(false);
          setTourScript(script);
          setTourOpen(true);
        },
        closeTour: () => {
          setTourOpen(false);
          // Sync "saw the guided tour" (fires on Done and on skip alike — the
          // signal is exposure, not completion). Fire-and-forget as above.
          apiRequest("PATCH", "/api/account/settings", { tourSeenAt: Date.now() }).catch(() => {});
          // First run ends here. The questionnaire is no longer chained on —
          // see the note above where finishFirstRun used to live.
        },
        presenceVisible,
        setPresenceVisible,
        pokemonDetailId,
        openPokemonDetail: (id: number) => setPokemonDetailId(id),
        closePokemonDetail: () => setPokemonDetailId(null),
        userPriorities,
        setUserPriorities,
        resetUserPriorities,
        featureWeights,
        nexQuestionnaireOpen,
        openLiveScan,
        openNexQuestionnaire,
        closeNexQuestionnaire,
        completeNexQuestionnaire,
        userState,
        dismissResumeBanner,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside SessionProvider");
  return ctx;
}

/**
 * Non-throwing variant: returns the session, or null when rendered outside a
 * SessionProvider (e.g. an isolated component unit test). Use this where a
 * component may legitimately render without the provider and can fall back to a
 * sensible default rather than crashing.
 */
export function useSessionOptional(): SessionState | null {
  return useContext(SessionContext);
}
